/**
 * Card da extensão Pronto Atendimento — espelho comportamental de HealthPlanCard (TISS).
 * Contract identifier: "prontoAtendimento"
 */
import {
  Stack,
  Typography,
  Button,
  useTheme,
  Divider,
  Box,
} from "@mui/material";
import { PrimaryButton } from "@/components/common";
import { ReactNode, useState } from "react";
import { useProntoAtendimentoContractContext } from "@/contexts/ProntoAtendimentoContractContext";
import { PostContract } from "@/services/fhir/contract/PostContract";
import { PatchContract } from "@/services/fhir/contract/PatchContract";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { MedicalServices } from "@mui/icons-material";
import { HealthPlanModals } from "./HealthPlanModals";
import dayjs from "dayjs";
import { useFormatter } from "@/utils/useFormatter";

interface ProntoAtendimentoExtensionCardProps {
  title: string;
  description: string;
  status?: string;
  children?: ReactNode;
  priceInfo?: string;
}

export function ProntoAtendimentoExtensionCard({
  title,
  description,
  status,
  children,
  priceInfo,
}: ProntoAtendimentoExtensionCardProps) {
  const { access, user } = useSessionContext();
  const { organization } = useOrganizationContext();
  const theme = useTheme();
  const { contractProntoAtendimento, refetchContract } =
    useProntoAtendimentoContractContext();
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenActivateModal = () => {
    setIsActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setIsActivateModalOpen(false);
  };

  const handleOpenCancelModal = () => {
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  const handleActivate = async () => {
    setIsLoading(true);
    try {
      // Gera data no fuso horário local subtraindo 3 horas (UTC-3) — mesmo TISS
      const currentDate = dayjs().subtract(3, "hour").toISOString();

      await PostContract({
        data: {
          resourceType: "Contract",
          extension: [
            {
              url: "https://fluxmed.com.br/fhir/extension/pronto_atendimento/cpf",
              valueString: user?.username || "",
            },
            {
              url: "https://fluxmed.com.br/fhir/extension/pronto_atendimento/nome",
              valueString: user?.name || "",
            },
            {
              url: "https://fluxmed.com.br/fhir/extension/pronto_atendimento/email",
              valueString: user?.email || "",
            },
          ],
          identifier: [
            {
              system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
              // Identifier estável da extensão Pronto Atendimento
              value: "prontoAtendimento",
            },
          ],
          status: "executed",
          authority: [
            {
              reference: `Organization/${organization?.id}`,
            },
          ],
          type: {
            coding: [
              {
                system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
                code: "prontoAtendimento",
              },
            ],
          },
          term: [
            {
              identifier: {
                system: "https://fluxmed.com.br/hapi/fhir/R5/contract-terms",
                value: "prontoAtendimento",
              },
              issued: currentDate,
              applies: {
                start: currentDate,
              },
              text: "Contrato de extensão para utilização do Pronto Atendimento",
              offer: {
                type: {
                  coding: [
                    {
                      system:
                        "https://fluxmed.com.br/hapi/fhir/R5/contract-offer-types",
                      code: "subscription",
                    },
                  ],
                },
              },
              asset: [
                {
                  valuedItem: [
                    {
                      entityCodeableConcept: {
                        coding: [
                          {
                            system:
                              "https://fluxmed.com.br/hapi/fhir/R5/subscription-types",
                            code: "subscription-fee",
                          },
                        ],
                      },
                      quantity: {
                        value: 1,
                        unit: "subscription",
                      },
                      unitPrice: {
                        value: 0,
                        currency: "BRL",
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      });

      toast.success("Extensão de Pronto Atendimento ativada com sucesso!");
      await refetchContract();
      setIsActivateModalOpen(false);
    } catch (error) {
      console.error("Erro ao ativar extensão de Pronto Atendimento:", error);
      toast.error("Erro ao ativar extensão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    setIsLoading(true);
    try {
      await PatchContract({
        contractId: contractProntoAtendimento?.id,
        operation: "replace",
        path: "/status",
        content: "cancelled",
      });

      toast.success("Extensão de Pronto Atendimento cancelada com sucesso!");
      await refetchContract();
      setIsCancelModalOpen(false);
    } catch (error) {
      console.error("Erro ao cancelar extensão de Pronto Atendimento:", error);
      toast.error("Erro ao cancelar extensão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Stack
        sx={{
          border: "1px solid",
          borderColor: "neutral700.main",
          borderRadius: "15px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          minHeight: "400px",
          paddingTop: 4,
          paddingBottom: 3,
        }}
      >
        {priceInfo && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              left: "0px",
              bgcolor: "secondary800.main",
              color: "white",
              padding: "4px 12px",
              borderTopRightRadius: "15px",
              borderBottomRightRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {priceInfo}
          </Stack>
        )}

        {status && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              right: "0px",
              bgcolor:
                status === "Em desenvolvimento"
                  ? "warning.main"
                  : "success.main",
              color: "white",
              padding: "4px 12px",
              borderTopLeftRadius: "15px",
              borderBottomLeftRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {status}
          </Stack>
        )}

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: "500",
              paddingBottom: 2,
              paddingLeft: 5,
              paddingRight: 5,
              paddingTop: 1,
              alignSelf: "center",
              textAlign: "center",
              position: "relative",
              zIndex: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack flexGrow={1} overflow="auto" maxHeight="330px">
          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{
              paddingBottom: 3,
              paddingLeft: 5,
              paddingRight: 5,
            }}
            align="left"
          >
            {description}
          </Typography>

          {children && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              {children}
            </Stack>
          )}

          {contractProntoAtendimento && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              <Divider sx={{ marginY: 2 }} />
              <Typography
                variant="body1"
                fontWeight="600"
                textAlign="center"
                gutterBottom
              >
                Responsável pela ativação
              </Typography>
              <Box
                sx={{
                  backgroundColor: "neutral100.main",
                  p: 2,
                  borderRadius: 2,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ mb: 1, wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Nome:</strong>{" "}
                  {contractProntoAtendimento?.extension?.find((e) =>
                    e.url?.includes("nome"),
                  )?.valueString || "-"}
                </Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mb: 1 }}>
                  <strong>CPF:</strong>{" "}
                  {useFormatter?.formatCPF(
                    contractProntoAtendimento?.extension?.find((e) =>
                      e.url?.includes("cpf"),
                    )?.valueString || "",
                  ) || "-"}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Email:</strong>{" "}
                  {contractProntoAtendimento?.extension?.find((e) =>
                    e.url?.includes("email"),
                  )?.valueString || "-"}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          {contractProntoAtendimento ? (
            <Button
              sx={{
                width: "70%",
                height: "40px",
                backgroundColor: access?.roles?.includes("owner")
                  ? "error700.main"
                  : "neutral300.main",
                color: "neutral0.main",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: access?.roles?.includes("owner")
                    ? "error700.main"
                    : "neutral300.main",
                },
                textTransform: "capitalize",
                fontSize: theme.typography.subtitle2.fontSize,
                "&:disabled": {
                  color: "neutral600.main",
                },
              }}
              startIcon={<span style={{ color: "white" }}>⚠️</span>}
              disabled={!access?.roles?.includes("owner")}
              onClick={handleOpenCancelModal}
            >
              {access?.roles?.includes("owner")
                ? "Cancelar Pronto Atendimento"
                : "Somente proprietários"}
            </Button>
          ) : (
            <PrimaryButton
              width="70%"
              height="40px"
              onClick={handleOpenActivateModal}
              startIcon={<MedicalServices />}
              // Mesmo bloqueio de produção da extensão TISS
              disabled={
                !access?.roles?.includes("owner")
              }
            >
              {access?.roles?.includes("owner")
                ? "Ativar Pronto Atendimento"
                : "Somente proprietários"}
            </PrimaryButton>
          )}
        </Stack>
      </Stack>

      {/* Reutiliza modais genéricos da TISS (só o agentName muda) */}
      <HealthPlanModals
        isActivateModalOpen={isActivateModalOpen}
        isCancelModalOpen={isCancelModalOpen}
        isLoading={isLoading}
        onCloseActivateModal={handleCloseActivateModal}
        onCloseCancelModal={handleCloseCancelModal}
        onConfirmActivate={handleActivate}
        onConfirmCancel={handleCancel}
        agentName={title}
      />
    </>
  );
}
